// Service pour gérer les outils de dessin avec Leaflet + Geoman
const DrawService = {
    map: null,
    collection: null,
    drawing: null,
    layerToSnap: [],
    featureGroup:null, 
    layerController : null,
    tagGroup:null, 
    resolveCreate: null, 
    onLayerSelect:null,
    intervaltool:null,
    measure:null,
    floatingTooltip:null,
    infoCollectionCur:null,
    typeCollectionCur:null,
    options: {
        zoomPadding: { paddingTopLeft: [800, 0] } // Format Leaflet: paddingTopLeft et paddingBottomRight
    },
  
    /**
     * Initialise le service avec une instance de carte Leaflet.
     * Doit être appelé avant toute autre méthode.
     * @param {L.Map} mapInstance - instance de la carte Leaflet
     * @param {L.featureGroup} featureGroup - instance de featuregroup Leaflet
     * @param {L.layer} layerController - instance de layer Leaflet
     * @param {Object} options - options du service (zoomPadding: [number, number])
     */
    init(mapInstance,featureGroup,layerController, options = {}) {
        this.map = mapInstance;
        this.layerController = layerController
        this.featureGroup = featureGroup;
        this.tagGroup = L.featureGroup();
        this.floatingTooltip = L.tooltip({
            permanent: false,
            sticky: true,
            className: 'custom-tooltip-floating',
            direction: 'bottom',
        });
        // Fusionne les options avec les valeurs par défaut
        this.options = { ...this.options, ...options };
    },
  
    /**
     * Active l'écoute d'événements de dessin avec Geoman pour :
     *  - Ajouter automatiquement les entités dessinées au `featureGroup` (groupe de calques Leaflet)
     *  - Gérer le snapping (accrochage) si des couches sont disponibles
     *  - Déclencher un événement personnalisé "sogefi:draw" avec les données utiles
     *
     * Cette méthode est conçue pour être utilisée avec **Leaflet-Geoman**, et non Leaflet-Draw.
     *
     * @param {string} type - Le type de géométrie en cours ('marker', 'polygon', 'polyline', etc.)
     */
    eventCreated(type, data) {
        if (!this.map) {
            console.error("DrawService: la carte n'est pas initialisée.");
            return;
        }
        if (!this.featureGroup) {
            console.error("DrawService : featureGroup non initialisé");
            return;
        }

        if(this.layerController?.instanceLayerCadastre){
            this.layerController.instanceLayerCadastre.setStatutCadastre(false);
        }

        // Nettoie les anciens écouteurs pour éviter les doublons
        this.map.off('pm:create');

        // Écoute la création d'une nouvelle entité via Geoman
        this.map.on('pm:create', (e) => {
            const layer = e.layer;      
            this.map.removeLayer(layer);

            // Attache la collection si elle a été fournie
            if (this.collection) {
                layer.collection = this.collection;
                layer.collection.id = layer._leaflet_id;
                
                layer.id = layer._leaflet_id;
                layer.sogefi_id = this.collection.id;
                layer.data = this.collection;
                if (layer.getRadius) {  
                    layer.data.options.radius = layer.getRadius();
                }       
                layer.data.id = layer._leaflet_id;
                layer.data.type = type;
                if(type === 'textMarker'){
                    layer.data.titre = 'Texte';
                }
                
                layer.data.geojson = layer.toGeoJSON();
                layer.data.geojson.properties = data;
                layer.data.geom = JSON.stringify(layer.toGeoJSON().geometry);
            }            
           
            // Active le snapping si des layers sont disponibles
            if (this.layerToSnap?.length > 0 && layer.pm?.enable) {
                layer.pm.setSnappable(true);
                clearInterval(this.intervaltool);
                this.intervaltool = null;
                layer.pm.setSnapDistance?.(20); // ajuster la distance d'accrochage si besoin
            }

            // Désactive le mode de dessin après création
            this.map.pm.disableDraw();


            // Appelle la Promise si définie
            if (this.resolveCreate) {
                this.map.off('mousemove'); 
                this.map.off('pm:drawstart');
                this.resolveCreate({
                    e,
                    layer,
                    layerType: type,
                    data: { collection: this.collection }
                });

                this.resolveCreate = null; 
                if(this.layerController?.instanceLayerCadastre){
                    this.layerController.instanceLayerCadastre.setStatutCadastre(true);
                }
            }
        });
        
    },

    clearAllEvent(){
        this.clearMeasureTooltip();
        if (!this.map) {
            console.error("DrawService: 'map' non initialisé. Appelle d'abord init().");
            return;
        }
        this.map.pm.disableDraw();
        this.map.off('mousemove'); 
        this.map.off('pm:drawstart');
        this.map.off('pm:create');
    },
  
    /**
     * Lance un mode de dessin avec Geoman
     * @param {object} options - options pour personnaliser
     * @param {object} collection - info de collection à associer au dessin
     */
    setDrawitem(options = {}, collection = null, typedraw, tiplab) {
        this.clearMeasureTooltip();
        if (!this.map) {
            console.error("DrawService: 'map' non initialisé. Appelle d'abord init().");
            return;
        }
        if (collection) this.collection = collection;
        
        this.map.off('pm:create');
        this.map.off('pm:drawstart');
        this.map.off('mousemove'); 
        if (this.featureGroup) {
            this.featureGroup.eachLayer((l) => {
                l.options.interactive = false; // désactive tout clic
                if (l.getElement) {
                    const el = l.getElement();
                    if (el) el.style.pointerEvents = 'none'; // renfort visuel
                }
            });
        }

        if(this.layerController?.instanceLayerCadastre){
            this.layerController.instanceLayerCadastre.setStatutCadastre(false);
        }
        
        if(this.intervaltool){
            clearInterval(this.intervaltool);
            this.intervaltool = null;
        }

        return new Promise((resolve) => {
            this.resolveCreate = resolve;

            // Active la logique de Leaflet Draw pour le snapping et l’événement personnalisé
            this.eventCreated(typedraw,collection);

            if(typedraw === 'circle' || typedraw === 'polyline'){
                this.makeOffMeasureTooltip(typedraw);
            }else{
                this.intervaltool = setInterval(() => { this.updateTooltip(typedraw,tiplab) }, 1); 
            }
        
            // Si un autre mode de dessin est actif, on le désactive
            if (this.map.pm.globalDrawModeEnabled()) {
                this.map.pm.disableDraw();
            }
        
            // Active le mode de dessin de Geoman 
            switch(typedraw) {
                case 'textMarker':
                case 'marker':this.activeCreateMarker(options);break;
                case 'polyline':this.activeCreatePolyline(options);break;
                case 'circle':this.activeCreateCircle(options);break;
                case 'rectangle':this.activeCreateRectangle(options);break;
                case 'polygon':this.activeCreatePolygon(options);break;
            }   
        });        
    },

    makeOffMeasureTooltip(typedraw){
        if (!this.map) {
            console.error("DrawService: la carte n'est pas initialisée.");
            return;
        }

        let texttip = 'Cliquez pour commencer à dessiner';
        if(typedraw === 'circle')texttip = 'Cliquez pour placer le centre du cercle';
        

        this.map.off('pm:drawstart');
        this.map.on('pm:drawstart', (e) => {
            const layer = e.workingLayer;
            this.floatingTooltip.setContent(texttip).setLatLng(this.map.getCenter());
            if (!this.map.hasLayer(this.floatingTooltip)) {
                this.floatingTooltip.addTo(this.map);
            }
            if (!layer._tooltip) {
                layer.bindTooltip("", { permanent: false, sticky: true,direction:'bottom' });
            }
            this.map.on('mousemove', (event) => {
                layer.setTooltipContent(texttip);
                layer.openTooltip(event.latlng); 
                this.floatingTooltip.setLatLng(event.latlng);
                switch(typedraw) {
                    case 'polyline':                        
                        const latlngs = layer.getLatLngs();
                        if (!latlngs.length)return;
                        const measure = this.getDistanceFromMove(event,layer);
                        if (latlngs.length < 2) {
                            layer.setTooltipContent('<b>Distance : '+measure+'</b><br/> Déplacez puis cliquez pour continuer à dessiner');
                        }else{
                            layer.setTooltipContent('<b>Distance : '+measure+'</b><br/>Double Cliquez pour terminer le dessin');
                        }
                        break;
                    case 'circle':
                        if (!layer.getLatLng) {
                            // par sécurité
                            return;
                        }
                        const radius = layer.getRadius();
                        if( radius > 0){
                            const rayon = this.getCircleRadius(layer);
                            layer.setTooltipContent('<b>Rayon : '+rayon+'</b><br/>Déplacez puis cliquez pour terminer le cercle');
                        }
                        break;
                    
                }   
               
                
            });
            
        });
    },

    clearMeasureTooltip() {
        if (this.floatingTooltip && this.map?.hasLayer(this.floatingTooltip)) {
            this.map.removeLayer(this.floatingTooltip);
        }
    
        this.map.off('mousemove');
        this.map.off('pm:drawstart');
    
    },

    getCircleRadius(layer) {
        if (!layer || !layer.getRadius) return '';
        const radius = layer.getRadius(); // en mètres
        return radius >= 1000 ? (radius / 1000).toFixed(2) + ' km' : radius.toFixed(0) + ' m';
    },

    getDistanceFromMove(event,layer){
        let measure = '';
        const latlngs = layer.getLatLngs();
        if (!latlngs.length)return;
        // dernier point = position actuelle de la souris
        const lastPoint = event.latlng;
        let distance = 0;

        for (let i = 0; i < latlngs.length - 1; i++) {
            distance += latlngs[i].distanceTo(latlngs[i + 1]);
        }

        // ajoute la distance jusqu'à la souris
        distance += latlngs[latlngs.length - 1].distanceTo(lastPoint);

        return distance >= 1000 ? (distance / 1000).toFixed(2) + ' km' : distance.toFixed(0) + ' m';
    },

    activeCreateMarker(options = {}) {        
        this.map.pm.enableDraw('Marker', {
            draggable: true,
            markerStyle: options?.icon ? { icon: options.icon } : {},
        });    
    },

    activeCreatePolyline(options = {}) {  
        this.map.pm.enableDraw('Line', {
            templineStyle: {
                color: options.color || "#92bbf4"
            },
            hintlineStyle: {
                color: options.color || "#92bbf4",
                dashArray: [5, 5]
            },
            pathOptions: {
                color: options.color || "#92bbf4",
                weight: options.weight || 3
            },
            snappable: true,
            snapDistance: 20,
            tooltips:false
        });
        
    },

    activeCreateCircle(options = {}) {        
        this.map.pm.enableDraw('Circle', {
            snappable: true,
            templineStyle: { color: options.color || "#92bbf4" },
            hintlineStyle: { color: options.color || "#92bbf4", dashArray: [5, 5] },
            tooltips:false
        });  
    },

    activeCreateRectangle(options = {}) {        
        this.map.pm.enableDraw('Rectangle', {
            snappable: true,
            allowSelfIntersection: false,
            templineStyle: { color: options.color || "#92bbf4" },
            hintlineStyle: { color: options.color || "#92bbf4", dashArray: [5, 5] },
        });    
        
    },
    
    activeCreatePolygon(options = {}) {        
        this.map.pm.enableDraw('Polygon', {
            snappable: true,
            templineStyle: { color: options.color || "#92bbf4" },
            hintlineStyle: { color: options.color || "#92bbf4", dashArray: [5, 5] },
        });    
        
    },

    updateTooltip(typedraw, tiplab){
        document.querySelectorAll('.leaflet-tooltip').forEach(tip => {
            const text = tip.innerText.trim();
            if(typedraw === 'textMarker' || typedraw === 'marker'){
                if (text === 'Click to place marker') {
                    tip.innerText = 'Cliquez pour placer un '+tiplab;
                }
            }else if(typedraw === 'rectangle'){
                if (text === 'Click to place first vertex') {
                    tip.innerText = 'Cliquez pour commencer à dessiner';
                }
                else if(text === 'Click to finish') {
                    tip.innerText = 'Déplacez puis cliquez pour terminer à dessiner';
                }
            }else if(typedraw === 'polygon'){
                if (text === 'Click to place first vertex') {
                    tip.innerText = 'Cliquez pour commencer à dessiner';
                }
                else if(text === 'Click to continue drawing') {
                    tip.innerText = 'Déplacez puis cliquez pour continuer à dessiner';
                }
                else if(text === 'Click first marker to finish') {
                    tip.innerText = 'Cliquez sur le premier point pour terminer à dessiner';
                }
            }else if(typedraw === 'modele'){
                tip.innerText = 'Placer le marqueur au centre de votre '+tiplab;
            }

        });
    },
    
    AddLayerCollection(layer,data,action){
        return new Promise((resolve) => {
            if (!this.featureGroup) {
                console.error("DrawService : featureGroup non initialisé");
                return;
            }
            if (!this.map) {
                console.error("DrawService : map non initialisée");
                return;
            }

            let alreadyExists = false;
            this.featureGroup.eachLayer((existingLayer) => {
                if (existingLayer.data && existingLayer.data.id === data.id) {
                    alreadyExists = true;
                }
            });

            if (alreadyExists) {
                // Si déjà existante → soit on la met à jour, soit on la remplace
                this.removeLayerCollection(data, 'entity'); // supprime l'ancienne
            }

            // Ajout des propriétés custom sur le layer
            if(data.type === 'textMarker' ){
                const htmlString = layer.options.icon.options.html;
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = htmlString;
                const span = tempDiv.firstChild;
                layer.options.icon.options.html = span;
            }
           
            layer.options = layer.options || {};
            layer.css = layer.css || {};
            layer.sogefi_id = data.id;
            layer.data = data;

            layer._originalStyle = {
                opacity: layer?.options?.opacity ?? 1,
                fillOpacity: layer?.options?.fillOpacity ?? 0.7
            };

            // On ajoute au featureGroup
            this.featureGroup.addLayer(layer);
            
            // Ajout du layer au groupe
            layer.addTo(this.featureGroup);

            // Si featureGroup n’est pas encore sur la carte, l’ajouter
            if (!this.map.hasLayer(this.featureGroup)) {
                this.map.addLayer(this.featureGroup);
            }

            if(action == 'edit'){
                this.featureGroup.eachLayer((l) => {
                    if (l !== layer) {
                      l.options.interactive = false; // désactive tout clic
                      if (l.getElement) {
                        const el = l.getElement();
                        if (el) el.style.pointerEvents = 'none'; // renfort visuel
                      }
                    }
                });

                if(this.layerController?.instanceLayerCadastre){
                    this.layerController.instanceLayerCadastre.setStatutCadastre(false);
                }
                if(this.typeCollectionCur == "collection" || this.infoCollectionCur?.edit){
                    if (layer.pm) {
                        if(layer.data.type === 'rectangle'){
                            layer.pm.enable({
                                hideMiddleMarkers: true,          // IMPORTANT : pas de ronds au milieu des côtés
                                preventMarkerRemoval: true,
                                allowSelfIntersection: false,
                                draggable: false,                 // on évite de déplacer tout le polygone en bloc
                                snappable: true,
                                snapDistance: 20
                              });
                            this.editRectangle(layer);
                        }else{
                            layer.pm.enable(); // active édition Geoman
                        }
                    }
                    if (layer.dragging) {
                        layer.dragging.enable(); // active déplacement si marqueur
                    }
                }
                if(layer.data.type === 'marker' || layer.data.type === 'textMarker'){
                    this.traceContourEdit(layer);
                }
                // this.zoomEntites(layer);  
            }
            
            if(action == 'zoom'){               
                this.zoomEntites(layer);                
            }

            if(layer.data.tag){
                this.addTagOnLayer(layer);                
            }

            if(layer.data.measure){
                this.addMeasureOnLayer(layer);                
            }
            
            if(layer.data.afficher_centroide){
                this.addCentroidOnLayer(layer);
            }
            
            if(action != 'edit'){
                // Ajouter ici l'événement click
                layer.on('click', () => {
                    // on appelle une fonction callback que tu passes à ton service
                    if (this.onLayerSelect) {
                        this.onLayerSelect(layer.data); 
                    }
                });
            }
          
        resolve(layer);
        });
    },

    zoomEntites(layer){
        let bounds;
        const padding = this.options.zoomPadding || { paddingTopLeft: [800, 0] };
        // Si le zoom actuel est supérieur à 18, on le garde pour éviter de reculer
        const currentZoom = this.map.getZoom();
        const maxZoom = currentZoom > 18 ? currentZoom : 18;
        const options = { ...padding, maxZoom };
        if (typeof layer.getBounds === 'function') {
            bounds = layer.getBounds();
            this.map.fitBounds(bounds, options);
        } else if (typeof layer.getLatLng === 'function') {
            const latlng = layer.getLatLng();
            bounds = L.latLngBounds(latlng, latlng);
            this.map.fitBounds(bounds, options);
        }
    },

    //cette fonction est appellé lors de l'edition d'un layer rectangle pour garder l'aspect du rectangle 
    editRectangle(layer) {
        if (!this.map) {
            console.error("DrawService: la carte n'est pas initialisée.");
            return;
        }
        layer.off('pm:markerdrag');
        layer.on('pm:markerdrag', (e) => {
            const moved = e.markerEvent?.target?.getLatLng?.();
            if (!moved) return;
    
            snapToAxisAlignedRect(moved);
        });
         // Petite aide : récupérer proprement les 4 coins (sans le dernier point fermé)
        const getCorners = () => {
            const ring = layer.getLatLngs()[0] || [];
            if (ring.length === 0) return [];
            // si le polygone est fermé, le dernier point == premier : on l’enlève
            const closed = ring.length >= 2 && ring[0].equals(ring[ring.length - 1]);
            const corners = closed ? ring.slice(0, ring.length - 1) : ring.slice();
            // on garde 4 coins
            return corners.slice(0, 4);
        };

        // Recalcule un rectangle aligné à partir de deux coins opposés
        const rectFromDiagonal = (a, c) => ([
            L.latLng(a.lat, a.lng),             // coin déplacé
            L.latLng(a.lat, c.lng),
            L.latLng(c.lat, c.lng),             // coin opposé
            L.latLng(c.lat, a.lng)
        ]);

        // Force le rectangle à rester rectangle à partir du sommet déplacé
        const snapToAxisAlignedRect = (movedLL) => {
            
            const corners = getCorners();
            if (corners.length < 4) return;

            // coin opposé = le plus éloigné du coin déplacé
            let opp = corners[0], maxD = -1;
            for (const pt of corners) {
            const d = movedLL.distanceTo(pt);
            if (d > maxD) { maxD = d; opp = pt; }
            }

            const newRing = rectFromDiagonal(movedLL, opp);

            // IMPORTANT : Leaflet attend un tableau d’anneaux pour un Polygon
            layer.setLatLngs([newRing]);
            layer.redraw();
        };
    },
    
    removeLayerCollection(collection,type){
        let ids = [];

        if (Array.isArray(collection)) {
            // collection peut être : [ {id:1}, {id:2} ] ou [1, 2, 3]
            ids = collection.map(c => {
                if (c === null || c === undefined) return c;
                // Si c est un objet avec id -> renvoyer c.id sinon renvoyer c (id direct)
                return (typeof c === 'object' && c.id !== undefined) ? c.id : c;
            }).filter(id => id !== undefined && id !== null);
        } else if (collection && typeof collection === "object") {
            // collection = { id: X }
            ids = [ collection.id ];
        } else if (collection !== undefined && collection !== null) {
            // collection est un id direct (ex: 123)
            ids = [ collection ];
        } else {
            console.warn("Collection mal formée :", collection);
            return;
        }
        if(type == 'collection'){
            this.featureGroup.eachLayer( (layer) => {
                if (ids.includes(layer.data.collection_id)) {
                    this.featureGroup.removeLayer(layer);
                    this.removeTagLayer(layer.data.id,'all');  
                }
            });
        }else{
            this.featureGroup.eachLayer( (layer) => {
                if (ids.includes(layer.data.id)) {
                    this.featureGroup.removeLayer(layer);
                    this.removeTagLayer(layer.data.id,'all');
                    this.clearHighlight(layer.data.id);      
                }
            });      
        }
        // Si plus de layers dans le featureGroup, enlève-le de la carte
        if (this.featureGroup.getLayers().length === 0) {
            this.map.removeLayer(this.featureGroup);
        }
    },

    desactiveEdition(){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return;
        }
        this.featureGroup.eachLayer( (layer) => {
                if (layer.pm) {
                    layer.pm.disable(); // active édition Geoman
                }
                if (layer.dragging) {
                    layer.dragging.disable(); // active déplacement si marqueur
                }
                if(layer.data.type === 'marker' || layer.data.type === 'textMarker'){
                    this.removeTagLayer(layer.data.id,'point');
                }        
                // Réactive l’interactivité visuelle et fonctionnelle
                layer.options.interactive = true;
                if (layer.getElement) {
                    const el = layer.getElement();
                    if (el) el.style.pointerEvents = 'auto'; // réactive les clics
                }      
                layer.on('click', () => {
                    // on appelle une fonction callback que tu passes à ton service
                    if (this.onLayerSelect) {
                        this.onLayerSelect(layer.data); 
                    }
                });  
        });
        
        if(this.layerController?.instanceLayerCadastre){
            this.layerController.instanceLayerCadastre.setStatutCadastre(true);
        }
    },

    noActiveEdition(layerData){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return;
        }
        
        this.featureGroup.eachLayer( (layer) => {
            if(layerData.id === layer.data.id){
                layer.pm.disable(); 
                layer.dragging.disable(); 
                layer.options.interactive = true;
                if (layer.getElement) {
                    const el = layer.getElement();
                    if (el) el.style.pointerEvents = 'auto'; 
                }
                
                if(layer.data.type === 'marker' || layer.data.type === 'textMarker'){
                    this.removeTagLayer(layer.data.id,'point');
                }        
            }     
        });
    },

    updatePropertieLayer(formvalue,idlayer,type){
        let updatedLayer = null;
        this.featureGroup.eachLayer((layer) => {
            if (layer.data && layer.data.id === idlayer) {
                layer.data.tag = formvalue.tag;
                layer.data.measure = formvalue.measure;
                layer.data.afficher_centroide = formvalue.afficher_centroide;
                if(type === 'marker'){
                    const objico = formvalue.iconClass.split(' ');
                    const objdata = {
                        icon:objico[1],
                        markerColor:formvalue.markerColor,
                        iconColor:formvalue.iconColor,
                        shape:formvalue.markerShape,
                        prefix:objico[0],
                        svg:true
                    }
                    const newIcon = this.createExtraMarkerUpdate(objdata);
                    layer.setIcon(newIcon);
                    layer.data.options.icon = newIcon;
                    // Déplacement du marqueur si lat/lng changés
                    if (formvalue.latitude && formvalue.longitude) {
                        const lat = parseFloat(formvalue.latitude);
                        const lng = parseFloat(formvalue.longitude);
                        layer.setLatLng([lat, lng]);
                        layer.data.latitude = lat;
                        layer.data.longitude = lng;
                    }
                }else if(type === 'textMarker'){
                    const htmlString = `<span style="margin-top:0px;margin-bottom:0px;width:max-content;display:inline-block;white-space:nowrap;${this.objectToInlineStyle({"color":formvalue.textColor,"font-size":formvalue.textSize+"px"})}">${formvalue.nomEntite}</span>`;
                    
                    const tempDiv = document.createElement("div");
                    tempDiv.innerHTML = htmlString;
                    const span = tempDiv.firstChild;
                    const newText =  L.divIcon({
                        className: "text-labels",
                        html: span
                    });                    
                    layer.setIcon(newText);
                    layer.data.options.icon = newText;
                    // Déplacement du marqueur si lat/lng changés
                    if (formvalue.latitude && formvalue.longitude) {
                        const lat = parseFloat(formvalue.latitude);
                        const lng = parseFloat(formvalue.longitude);
                        layer.setLatLng([lat, lng]);
                        layer.data.latitude = lat;
                        layer.data.longitude = lng;
                    }
                }else if (type === 'polyline') {
                    // Modifier la couleur, l'épaisseur (weight) et l'opacité
                    if (formvalue.lineColor) {
                        layer.setStyle({ color: formvalue.lineColor });
                        layer.data.options.color = formvalue.lineColor;
                    }
                    if (formvalue.lineEpaisseur) {
                        layer.setStyle({ weight: formvalue.lineEpaisseur });
                        layer.data.options.weight = formvalue.lineEpaisseur;
                    }
                    if (formvalue.lineOpacity) {
                        layer.setStyle({ opacity: formvalue.lineOpacity });
                        layer.data.options.opacity = formvalue.lineOpacity;
                    }
                } else if (type === 'polygon' ) {
                    // Modifier couleur, épaisseur, opacité pour polygones et rectangles
                    if (formvalue.polycontourColor) {
                        layer.setStyle({ color: formvalue.polycontourColor });
                        layer.data.options.color = formvalue.polycontourColor;
                    }
                    if (formvalue.polycontourEpaisseur) {
                        layer.setStyle({ weight: formvalue.polycontourEpaisseur });
                        layer.data.options.weight = formvalue.polycontourEpaisseur;
                    }
                    if (formvalue.polycontourOpacity) {
                        layer.setStyle({ opacity: formvalue.polycontourOpacity });
                        layer.data.options.opacity = formvalue.polycontourOpacity;
                    }
    
                    // remplissage (fillColor et fillOpacity)
                    if (formvalue.polyColor) {
                        layer.setStyle({ fillColor: formvalue.polyColor });
                        layer.data.options.fillColor = formvalue.polyColor;
                    }
                    if (formvalue.polyOpacity) {
                        layer.setStyle({ fillOpacity: formvalue.polyOpacity });
                        layer.data.options.fillOpacity = formvalue.polyOpacity;
                    }

                    if (formvalue.radius && formvalue.radius > 0) {
                        layer.setRadius(formvalue.radius);
                        layer.data.options.radius = formvalue.radius;
                    }
                }
                layer.data.titre = formvalue.nomEntite;
                layer.data.observation = formvalue.observation;
                updatedLayer = layer; 

                if(layer.data.tag){
                    this.addTagOnLayer(layer);
                }
                
                if(layer.data.measure){
                    this.addMeasureOnLayer(layer);                
                }

                if(layer.data.afficher_centroide){
                    this.addCentroidOnLayer(layer);
                }

            }            
        });
        return updatedLayer;
    },
    
    createExtraMarkerUpdate({icon = "", markerColor = "#252525", iconColor = "#ffffff", shape = "circle", prefix = "fa", svg = true}){
        if(icon == " " || icon == "") icon = "";
        return L.ExtraMarkers.icon({
            icon: icon,
            markerColor: markerColor,
            iconColor: iconColor,
            shape: shape,
            prefix: prefix,
            svg:svg
        });
    },

    createExtraMarker({icon = "fa-circle", markerColor = "#252525", iconColor = "#ffffff", shape = "circle", prefix = "fa", svg = true}){
        if(icon == " " || icon == "") icon = "fa-circle";
        return L.ExtraMarkers.icon({
            icon: icon,
            markerColor: markerColor,
            iconColor: iconColor,
            shape: shape,
            prefix: prefix,
            svg:svg
        });
    },
    
    objectToInlineStyle(styleObj) {
        return Object.entries(styleObj)
            .map(([key, value]) => `${this.camelToKebab(key)}:${value}`)
            .join(";");
    },
    
    camelToKebab(str) {
        return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    },

    zoomCollection(geojsonData){
        const padding = this.options.zoomPadding || { paddingTopLeft: [800, 0] };
        // Si le zoom actuel est supérieur à 18, on le garde pour éviter de reculer
        const currentZoom = this.map.getZoom();
        const maxZoom = currentZoom > 18 ? currentZoom : 18;
        const options = { ...padding, maxZoom };
        
        const layerGroup = L.geoJSON(geojsonData);
        const bounds = layerGroup.getBounds();
        this.map.fitBounds(bounds, options);
    },

    getLayerEdit(idlayer) {
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
    
        let foundLayer = null;
        this.featureGroup.eachLayer((layer) => {            
            if (layer?.data?.id === idlayer) {
                foundLayer = layer;
            }
        });
    
        return foundLayer;
    },

    removeTagLayer(layerid,type){
        this.tagGroup.eachLayer( (layertag) => {
            if(type === 'tag'){
                if("tagMarker_"+layerid == layertag.id){
                    this.tagGroup.removeLayer(layertag);
                }
            }else if(type === 'center'){
                if("tagMarkercntr_"+layerid == layertag.id){
                    this.tagGroup.removeLayer(layertag);
                }
            }else if(type === 'measure'){
                if("measureMarker_"+layerid == layertag.id){
                    this.tagGroup.removeLayer(layertag);
                }
            }else if(type === 'point'){
                if("pointMarker_"+layerid == layertag.id){
                    this.tagGroup.removeLayer(layertag);
                }
            }else if (type === 'line') {
                // Vérifie préfixe 'lineMarker_' et suffixe '_layerid'
                const prefix = "lineMarker_";
                const suffix = "_" + layerid;
                if (layertag.id.startsWith(prefix) && layertag.id.endsWith(suffix)) {
                    this.tagGroup.removeLayer(layertag);
                }
            }else if (type === 'all') {
                // Supprime tout ce qui se termine par _layerid
                if (layertag.id && layertag.id.endsWith("_" + layerid)) {
                    this.tagGroup.removeLayer(layertag);
                }
            }
        });
    },

    addCentroidOnLayer(layer){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
        if (!this.map.hasLayer(this.tagGroup)) {
            this.map.addLayer(this.tagGroup);
        }
        this.removeTagLayer(layer.data.id,'center');
        const geojson = layer.toGeoJSON();
        const center = turf.centerOfMass(geojson);
        const [lng, lat] = center.geometry.coordinates;
       
        const marker = L.circleMarker([lat, lng], {
            radius: 5,            
            color: "#000000",
            fillColor: "#ff0000",
            fillOpacity: 0.4,
            weight: 1
        });
    
        marker.layerType = "tagMarkercntr";
        marker.id = "tagMarkercntr_"+layer.data.id;
        marker.addTo(this.tagGroup);
    },

    addTagOnLayer(layer){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
        if (!this.map.hasLayer(this.tagGroup)) {
            this.map.addLayer(this.tagGroup);
        }
        this.removeTagLayer(layer.data.id,'tag');
        let latlng;
        if (layer.data.type == "circle" || layer.data.type == "marker")  latlng = layer.getLatLng();
        else latlng = layer.getBounds().getCenter(); 
        let point = this.map.latLngToLayerPoint(latlng); // convertit latlng en pixels sur la map
        point.y += 5;                                  // décalage de 12 pixels vers le haut
        let newLatLng = this.map.layerPointToLatLng(point);

        const htmlString = `<span style=" transform: translate(-35%,23%);white-space: nowrap;color: #333;font-size: 11px;display: inline-block;position: relative;text-shadow: 0px 0px 10px #c7bcbc;font-weight: bold;background: #ffffff7a;padding: 0 2px;">${layer.data.titre}</span>`;
        
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlString;
        const span = tempDiv.firstChild;
        let tagLayer = L.marker(newLatLng, {
            icon: L.divIcon({
                className: "tagLabelCtn",
                html: span
            })
        });
        tagLayer.layerType = "tagMarker";
        tagLayer.id = "tagMarker_"+layer.data.id;
        tagLayer.addTo(this.tagGroup);
    },

    traceContourEdit(layer) {
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
        if (!this.map.hasLayer(this.tagGroup)) {
            this.map.addLayer(this.tagGroup);
        }
    
        const latlng = layer.getLatLng();
        this.removeTagLayer(layer.data.id, "point");
    
        let width = 40;
        let height = 60;
        let posx = width / 2;
        let posy = height - 12 ;
    
        if (layer.data.type === "textMarker") {
            const el = layer.getElement();
            if (el) {
                const inner = el.firstChild;
                if (inner) {
                    const rect = inner.getBoundingClientRect();
                    width = rect.width + 16;
                    height = rect.height + 8;
                    posx = 12;
                    posy = 8;
                }
            }
        }
    
        // Création d’un "marker" factice qui sera juste un contour en CSS
        const outline = L.marker(latlng, {
            interactive: false,
            icon: L.divIcon({
                html: `<div style="
                    width:${width}px;
                    height:${height}px;
                    border:4px dashed #DD88A0;
                    border-radius:4px;
                    box-sizing:border-box;
                    pointer-events:none;
                "></div>`,
                className: "", 
                iconSize: [width, height],
                iconAnchor: [posx, posy], 
            }),
        });
    
        outline.layerType = "tagMarker";
        outline.id = "pointMarker_" + layer.data.id;
        outline.addTo(this.tagGroup);
    },

    affichecentroid(layer){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
        this.removeTagLayer(layer.data.id,'center');
        let latlng;
        if (layer.data.type == "circle"){
            latlng = layer.getLatLng();
        }else{
            latlng = layer.getBounds().getCenter(); 
        }

        let circleMarker = L.circleMarker(latlng, {
            color: 'blue',
            fillColor: '#30f',
            fillOpacity: 0.7,
            radius: 10 
        });
        
        circleMarker.layerType = "tagMarker";
        circleMarker.id = "tagMarkercntr_"+layer.data.id;
        circleMarker.addTo(this.tagGroup);
    },

    addMeasureOnLayer(layer){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }
        if (layer.data.type != 'polyline'){
            let measure = '';
            if (!this.map.hasLayer(this.tagGroup)) {
                this.map.addLayer(this.tagGroup);
            }
            this.removeTagLayer(layer.data.id,'measure');
            let latlng;
            if (layer.data.type == "circle"){
                latlng = layer.getLatLng();
                 // Cercle : créer un polygone approximatif avec turf
                const center = [latlng.lng, latlng.lat];
                const radius = layer.getRadius(); // en mètres
                const circlePolygon = turf.circle(center, radius, { steps: 64, units: 'meters' });                
                const areaM2 = turf.area(circlePolygon); // en m²
                measure = areaM2 >= 1_000_000
                ? (areaM2 / 1_000_000).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                .replace(/\u00A0/g, " ")  + ' km²'
                : Math.round(areaM2).toLocaleString("fr-FR").replace(/\u00A0/g, " ") + " m²";
            }  
            else {
                if(!layer.getBounds)return;
                latlng = layer.getBounds().getCenter(); 
                let latlngs = layer.getLatLngs();
                if (latlngs[0] instanceof Array) latlngs = latlngs[0];
            
                if (!latlngs || latlngs.length < 3) return 0;
            
                const coordinates = latlngs.map(ll => [ll.lng, ll.lat]);
                // fermer le polygone si nécessaire
                if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
                    coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
                    coordinates.push(coordinates[0]);
                }
            
                const polygon = turf.polygon([coordinates]);
                const areaM2 = turf.area(polygon); // en m²
                measure = areaM2 >= 1_000_000
                ? (areaM2 / 1_000_000).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                .replace(/\u00A0/g, " ")  + ' km²'
                : Math.round(areaM2).toLocaleString("fr-FR").replace(/\u00A0/g, " ") + " m²";
            }

            let point = this.map.latLngToLayerPoint(latlng); // convertit latlng en pixels sur la map
            point.y -= 5;                                  // décalage de 12 pixels vers le haut
            let newLatLng = this.map.layerPointToLatLng(point);
            const htmlString = `<span style=" transform: translate(-35%, -65%);white-space: nowrap;color: #333;font-size: 11px;display: inline-block;position: relative;text-shadow: 0px 0px 10px #c7bcbc;font-weight: bold;background: #ffffff7a;padding: 0 2px;">${measure}</span>`;
            
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlString;
            const span = tempDiv.firstChild;
            let tagLayer = L.marker(newLatLng, {
                icon: L.divIcon({
                    className: "tagLabelCtn",
                    html: span
                })
            });
            tagLayer.layerType = "tagMarker";
            tagLayer.id = "measureMarker_"+layer.data.id;
            tagLayer.addTo(this.tagGroup);
        }
       

        if (layer.data.type != "circle"){
            this.addMeasureinLineOnLayer(layer);
        }
    },
    
    addMeasureinLineOnLayer(layer){        
        if (!this.map.hasLayer(this.tagGroup)) {
            this.map.addLayer(this.tagGroup);
        }
        const alatlngs = layer.getLatLngs();
        let latlngs = null;
        if(layer.data.type === 'polyline'){
            latlngs = alatlngs;
        }else{
            latlngs = alatlngs[0].slice();
            latlngs.push(L.latLng(latlngs[0].lat, latlngs[0].lng));
        }
        if (latlngs.length < 2) return;

        this.removeTagLayer(layer.data.id,'line');

        for (let i = 0; i < latlngs.length - 1; i++) {
            const start = latlngs[i];
            const end = latlngs[i + 1];

            // Milieu du segment
            const midLat = (start.lat + end.lat) / 2;
            const midLng = (start.lng + end.lng) / 2;
            const midPoint = L.latLng(midLat, midLng);

            // Angle pour la rotation en degrés
            const p1 = this.map.latLngToLayerPoint(start);
            const p2 = this.map.latLngToLayerPoint(end);

            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const angleRad = Math.atan2(dy,dx);
            let angleDeg = angleRad * (180 / Math.PI);

            // Ajustement pour que le texte ne soit jamais à l'envers
            if (angleDeg > 90) angleDeg -= 180;
            if (angleDeg < -90) angleDeg += 180;

            // Calcul de la distance
            const distance = start.distanceTo(end);
            const measure = distance >= 1000
            ? (distance / 1000).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\u00A0/g, " ") + " km"
            : Math.round(distance).toLocaleString("fr-FR").replace(/\u00A0/g, " ") + " m";

            // Créer le DivIcon
            const icon = L.divIcon({
                className: 'line-text-marker',
                html: `<div style="transform: rotate(${angleDeg}deg); white-space: nowrap;">${measure}</div>`
            });

            // Ajouter le marker au milieu du segment
            let tagLayer = L.marker(midPoint, { icon: icon, interactive: false });
            
            tagLayer.layerType = "tagMarker";
            tagLayer.id = "lineMarker_"+i+"_"+layer.data.id;
            tagLayer.addTo(this.tagGroup);
        }

    },

    redrawEntityCollection(idcollection) {
        // Parcours tous les layers du featureGroup
        this.featureGroup.eachLayer((layer) => {
            // Vérifie l'ID de la collection du layer
            const isTarget = !idcollection || layer.data?.collection_id === idcollection;
    
            // Style par défaut pour tous les layers non ciblés
            if (layer.setStyle) {
                // Polygone / rectangle / ligne
                layer.setStyle({
                    opacity: isTarget ? layer?._originalStyle?.opacity : 0.2,
                    fillOpacity: isTarget ? layer?._originalStyle?.fillOpacity : 0.2, // plus visible si ciblé
                });
            } else if (layer.setOpacity) {
                // Marker / circleMarker
                layer.setOpacity(isTarget ? 1 : 0.5);
            }
        });
    },

    addOriginLayerFile(layerAdd,iddCollectionSelected,userId){
        const layersCreated = [];
        let iter = 0;
        layerAdd.forEach((objlayer) => {   
            let layer;           
            if(objlayer.geom.type == 'Point'){
                layer = this.addPointLayer(objlayer,iddCollectionSelected,userId);
            }else{
                layer = this.addPolygonLayer(objlayer,iddCollectionSelected,userId);
            }
            if (layer){
                layersCreated.push(layer);
            } 
            iter = iter + 1;
        });
        return layersCreated;
    },

    creatDataAnnotation(options,typelay,titre,iddCollectionSelected,userId,observationplus){
        const newDataAnnotation = {
            collection_id: iddCollectionSelected,
            css: "null",
            measure: false,
            nb_fichier:0,
            observation: observationplus,
            options: options,
            sogefi_date: new Date().toISOString(),
            sogefi_date_modification: '',
            sogefi_operateur_creation: userId ?? null,
            sogefi_operateur_modification: '',
            tag: false,
            titre: titre,
            type: typelay,
        };
        return newDataAnnotation;
    },

    addPointLayer(objlayer,iddCollectionSelected,userId){
        const options = {
            icon: L.ExtraMarkers.icon({
                icon: 'fa-circle',
                markerColor: '#252525',
                iconColor: '#FFFFFF',
                shape: 'circle',
                prefix: 'fa',
                svg: true,
            }),
        };
        const newDataAnnotation = this.creatDataAnnotation(options,"marker",objlayer.titre,iddCollectionSelected,userId,objlayer.observation);
        const layer = L.marker( [parseFloat(objlayer.geom.coordinates[1]), parseFloat(objlayer.geom.coordinates[0])], {
            icon: options.icon
        });
        this.featureGroup.addLayer(layer);

        layer.id = layer._leaflet_id;
        layer.data = newDataAnnotation;      
        layer.data.id = layer._leaflet_id;
        layer.data.geojson = layer.toGeoJSON();
        layer.data.geojson.properties = newDataAnnotation;
        layer.data.geom = JSON.stringify(layer.toGeoJSON().geometry);

        // Si featureGroup n’est pas encore sur la carte, l’ajouter
        if (!this.map.hasLayer(this.featureGroup)) {
            this.map.addLayer(this.featureGroup);
        }
        return layer;
    },

    addPolygonLayer(objlayer,iddCollectionSelected,userId){
        const geojsonFeatures = {
            type: "Feature",
            geometry: objlayer.geom,
            properties: { titre: objlayer.titre }
        };      
        const options = {
            editing: {
                className: undefined,
            },
            color: "#92bbf4",
        };  
        let geomtype = 'polygon';
        let geomtype2 = 'Polygon';
        if(objlayer.geom.type === 'MultiLineString'){
            geomtype = 'polyline';
            geomtype2 = 'LineString';
        }

        const newDataAnnotation = this.creatDataAnnotation(options,geomtype,objlayer.titre,iddCollectionSelected,userId,objlayer.observation);
        const layer = L.geoJSON(geojsonFeatures, {
            style: {
            color: "#92bbf4",
            weight: 2,
            fillOpacity: 0.3
            }
        });
        this.featureGroup.addLayer(layer);

        layer.id = layer._leaflet_id;
        layer.data = newDataAnnotation;      
        layer.data.id = layer._leaflet_id;
        const geojson = layer.toGeoJSON();

        let datageo = {};
        if (geojson.features.length > 0) {
            let coord = {};
            coord.type = geomtype2;
            coord.coordinates =  geojson.features[0].geometry.coordinates[0];
            datageo.type = "Feature";
            datageo.geometry = coord;
            layer.data.geom = JSON.stringify(coord);
            layer.data.geojson = datageo;  
        }    
        layer.data.geojson.properties = newDataAnnotation;       

        // Si featureGroup n’est pas encore sur la carte, l’ajouter
        if (!this.map.hasLayer(this.featureGroup)) {
            this.map.addLayer(this.featureGroup);
        }
        return layer;
    },

    setZoomEmprise(bounds){
        if (!this.map) {
            console.error("DrawService : map non initialisée");
            return null;
        }        
        const padding = this.options.zoomPadding || { paddingTopLeft: [800, 0] };
        // Si le zoom actuel est supérieur à 18, on le garde pour éviter de reculer
        const currentZoom = this.map.getZoom();
        const maxZoom = currentZoom > 18 ? currentZoom : 18;
        const options = { ...padding, maxZoom };

        this.map.fitBounds(bounds,options);
    },

    highlightEntityCollection(entity){
        let alreadyExists = false;
        this.featureGroup.eachLayer((existingLayer) => {
            if (existingLayer.data && existingLayer.data.id === entity.id) {
                alreadyExists = true;
                if(this.highlightLayer){
                    const targetId = "highlight_"+entity.id;    
                    this.highlightLayer.eachLayer((layer) => {
                        if (layer.id === targetId) {
                            this.highlightLayer.removeLayer(layer);
                        }
                    });
                }
            }
        });
        if(alreadyExists){
            this.highlightEntity(entity);
        }

    },

    highlightEntity(entity) {
        if (!this.highlightLayer) {
            this.highlightLayer = L.layerGroup().addTo(this.map);
        }
        const color = 'yellow';
        const geom = entity.geojson.geometry;
        if(geom.type === "Point"){
            if (entity.type === "textMarker") {
                this.featureGroup.eachLayer((existingLayer) => {
                    if (existingLayer.data && existingLayer.data.id === entity.id) {
                        const el = existingLayer.getElement();
                        const latlng = [entity.geojson.geometry.coordinates[1], entity.geojson.geometry.coordinates[0]];
                        if (el) {                    
                            const inner = el.firstChild;
                            if (inner) {
                                const rect = inner.getBoundingClientRect();
                                const width = rect.width + 16;
                                const height = rect.height + 8;
                                const posx = 12;
                                const posy = 8;
                                 // Création d’un "marker" factice qui sera juste un contour en CSS
                                const textHighlight = L.marker(latlng, {
                                    interactive: false,
                                    icon: L.divIcon({
                                        html: `<div style="
                                            width:${width}px;
                                            height:${height}px;
                                            border: 3px solid ${color};
                                            background:rgba(255, 255, 0, 0.3);
                                            border-radius: 6px;
                                            pointer-events:none;
                                        "></div>`,
                                        className: "", 
                                        iconSize: [width, height],
                                        iconAnchor: [posx, posy], 
                                    }),
                                });
                            
                                textHighlight.id = "highlight_"+entity.id;
                                this.highlightLayer.addLayer(textHighlight);
                            }
                        }
                        
                        return;
                    }
                });               
            }else if (entity.type === 'marker') {
                // cas point            
                const latlng = [geom.coordinates[1], geom.coordinates[0]]; 
                const width = 40;
                const height = 60;
                const posx = width / 2;
                const posy = height - 12 ;
                 // Création d’un "marker" factice qui sera juste un contour en CSS
                 const textHighlight = L.marker(latlng, {
                    interactive: false,
                    icon: L.divIcon({
                        html: `<div style="
                            width:${width}px;
                            height:${height}px;
                            border: 3px solid ${color};
                            background:rgba(255, 255, 0, 0.3);
                            border-radius: 6px;
                            pointer-events:none;
                        "></div>`,
                        className: "", 
                        iconSize: [width, height],
                        iconAnchor: [posx, posy], 
                    }),
                });
                textHighlight.id = "highlight_"+entity.id;
                this.highlightLayer.addLayer(textHighlight);
            }else if(entity.type === 'circle'){
                const coords = entity.geojson.geometry.coordinates;
                const radius = entity.geojson.properties.options.radius;
                const circleHighlight = L.circle([coords[1], coords[0]], {
                    radius: radius * 1.1,
                    color: color,
                    weight: 4,
                    opacity: 0.8,
                    fillColor: color,
                    fillOpacity: 0.3,
                    interactive: false
                });
            
                circleHighlight.id = "highlight_"+entity.id;
                this.highlightLayer.addLayer(circleHighlight);
            }
        }else{
            // cas polygone / ligne
            const layerhighlight = L.geoJSON(entity.geojson, {
                style: {
                    color:color,
                    weight: 15,
                    opacity: 0.3,
                    fillColor: color,
                    fillOpacity: 0.3
                },
                interactive: false
            });
            
            layerhighlight.id = "highlight_"+entity.id;
            this.highlightLayer.addLayer(layerhighlight);
        }    
    },

    getPxForCm(cm = 5){
        const div = document.createElement('div');
        div.style.width = `${cm}cm`;
        div.style.position = 'absolute';
        document.body.appendChild(div);
        const px = div.getBoundingClientRect().width;
        document.body.removeChild(div);
        return px;
    },

    clearHighlight(id = null) {
        if (!this.highlightLayer) return;
    
        if (id === null) {
            //tout effacer
            this.highlightLayer.clearLayers();
        } else {
            // Supprimer uniquement le layer correspondant à l'id
            const targetId = "highlight_"+id;    
            this.highlightLayer.eachLayer((layer) => {
                if (layer.id === targetId) {
                    this.highlightLayer.removeLayer(layer);
                }
            });
        }
    },
    
    updateStyleLayer(formvalue,idlayer,type){
        this.featureGroup.eachLayer((layer) => {
            if (layer.data && layer.data.id === idlayer) {
                if(type === 'marker'){                
                    if(formvalue.polyColor){
                        const objdata = {
                            icon:layer.options.icon.options.icon,
                            markerColor:formvalue.polyColor,
                            iconColor:layer.options.icon.options.iconColor,
                            shape:layer.options.icon.options.shape,
                            prefix:layer.options.icon.options.prefix,
                            svg:true
                        }
                        const newIcon = this.createExtraMarker(objdata);
                        layer.setIcon(newIcon);
                        layer.data.options.icon = newIcon;
                    }
                }else if(type === 'textMarker'){          
                    if(formvalue.polyColor){
                        const htmlString = `<span style="margin-top:0px;margin-bottom:0px;width:max-content;display:inline-block;white-space:nowrap;${this.objectToInlineStyle({"color":formvalue.polyColor,"font-size":layer.data.css["font-size"]})}">${layer.data.titre}</span>`;
                        
                        const tempDiv = document.createElement("div");
                        tempDiv.innerHTML = htmlString;
                        const span = tempDiv.firstChild;
                        const newText =  L.divIcon({
                            className: "text-labels",
                            html: span
                        });                    
                        layer.setIcon(newText);
                        layer.data.options.icon = newText;
                    }
                }else if (type === 'polyline') {
                    // Modifier la couleur, l'épaisseur (weight) et l'opacité
                    if (formvalue.polycontourColor) {
                        layer.setStyle({ color: formvalue.polycontourColor });
                        layer.data.options.color = formvalue.polycontourColor;
                    }
                    if (formvalue.polycontourEpaisseur && formvalue.polycontourEpaisseur != 0) {
                        layer.setStyle({ weight: formvalue.polycontourEpaisseur });
                        layer.data.options.weight = formvalue.polycontourEpaisseur;
                    }
                    if (formvalue.polycontourOpacity) {
                        layer.setStyle({ opacity: formvalue.polycontourOpacity });
                        layer.data.options.opacity = formvalue.polycontourOpacity;
                    }
                } else if (type === 'polygon' || type === 'circle' || type === 'rectangle') {
                    // Modifier couleur, épaisseur, opacité pour polygones et rectangles
                    if (formvalue.polycontourColor) {
                        layer.setStyle({ color: formvalue.polycontourColor });
                        layer.data.options.color = formvalue.polycontourColor;
                    }
                    if (formvalue.polycontourEpaisseur && formvalue.polycontourEpaisseur != 0) {
                        layer.setStyle({ weight: formvalue.polycontourEpaisseur });
                        layer.data.options.weight = formvalue.polycontourEpaisseur;
                    }
                    if (formvalue.polycontourOpacity) {
                        layer.setStyle({ opacity: formvalue.polycontourOpacity });
                        layer.data.options.opacity = formvalue.polycontourOpacity;
                    }
    
                    // remplissage (fillColor et fillOpacity)
                    if (formvalue.polyColor) {
                        layer.setStyle({ fillColor: formvalue.polyColor });
                        layer.data.options.fillColor = formvalue.polyColor;
                    }
                    if (formvalue.polyOpacity) {
                        layer.setStyle({ fillOpacity: formvalue.polyOpacity });
                        layer.data.options.fillOpacity = formvalue.polyOpacity;
                    }
                }    
            }      
        });
    },    

    deleteLayerEntityModele(targetId) {
        if (!this.featureGroup) return;    
        // Supprimer uniquement le layer correspondant à l'id
        this.featureGroup.eachLayer((layer) => {
            if (layer.id === targetId) {
                this.featureGroup.removeLayer(layer);
            }
        });
    },

    createEntityModele(data,collection){
        if (!this.map) {
            console.error("DrawService: 'map' non initialisé. Appelle d'abord init().");
            return;
        }
        if (!this.featureGroup) {
            console.error("DrawService : featureGroup non initialisé");
            return;
        }
        if (collection) this.collection = collection;
        
        this.map.off('pm:create');
        this.map.off('pm:drawstart');
        this.map.off('mousemove'); 
        if (this.featureGroup) {
            this.featureGroup.eachLayer((l) => {
                l.options.interactive = false; // désactive tout clic
                if (l.getElement) {
                    const el = l.getElement();
                    if (el) el.style.pointerEvents = 'none'; // renfort visuel
                }
            });
        }

        if(this.layerController?.instanceLayerCadastre){
            this.layerController.instanceLayerCadastre.setStatutCadastre(false);
        }
        
        if(this.intervaltool){
            clearInterval(this.intervaltool);
            this.intervaltool = null;
        }
        return new Promise((resolve, reject) => {
            switch (data.type) {
                case 'rectangle':
                    this.activeCreateMarker();
                    const tiplab = "rectangle "+data.description;
                    this.intervaltool = setInterval(() => { this.updateTooltip("modele",tiplab) }, 1); 
                    this.map.on('pm:create', (e) => {
                        this.map.pm.disableDraw();
                        this.map.removeLayer(e.layer);
                        const rectangle = this.createModeleRectangle(e, data);
                        clearInterval(this.intervaltool);
                        this.intervaltool = null;
                        resolve(rectangle);
                    });
                    
                break;
            }
        });
    },

    createModeleRectangle(e, data) {
        const dimension = JSON.parse(data.dimension);
        const options =  JSON.parse(data.options);

        const rectangleLength = dimension.length;
        const rectangleWidth = dimension.width;
    
        const center = [e.layer._latlng.lng, e.layer._latlng.lat]; // Turf utilise [lng, lat]

        // Crée un rectangle Turf centré sur le point
        // turf.rectangle n'existe pas directement → on fait bbox autour du centre
        const halfLength = rectangleLength / 2;
        const halfWidth = rectangleWidth / 2;
        
        // Calcul des coins exacts en mètres
        const north = turf.destination(center, halfLength, 0, { units: 'meters' });
        const south = turf.destination(center, halfLength, 180, { units: 'meters' });
        const east  = turf.destination(center, halfWidth, 90, { units: 'meters' });
        const west  = turf.destination(center, halfWidth, 270, { units: 'meters' });
        
        // Polygon Turf
        const rectangleGeoJSON = turf.polygon([[
          [west.geometry.coordinates[0], north.geometry.coordinates[1]], // NW
          [east.geometry.coordinates[0], north.geometry.coordinates[1]], // NE
          [east.geometry.coordinates[0], south.geometry.coordinates[1]], // SE
          [west.geometry.coordinates[0], south.geometry.coordinates[1]], // SW
          [west.geometry.coordinates[0], north.geometry.coordinates[1]]  // fermer le polygone
        ]]);

        // Remplace rectangleBounds
        const rectangleBounds = L.geoJSON(rectangleGeoJSON).getBounds();
    
        options['transform'] = true;
    
        const rectangle = L.rectangle(rectangleBounds, options);
        rectangle.addTo(this.featureGroup);
        
        if (!this.map.hasLayer(this.featureGroup)) {
            this.map.addLayer(this.featureGroup);
        }
    
        if (this.collection) {
            rectangle.collection = this.collection;
            rectangle.collection.id = rectangle._leaflet_id;
            rectangle.id = rectangle._leaflet_id;
            rectangle.sogefi_id = this.collection.id;
            rectangle.data = this.collection;
           
            rectangle.data.id = rectangle._leaflet_id;
            rectangle.data.titre = data.libelle;
            rectangle.data.observation = data.description;
            
            rectangle.data.geojson = rectangle.toGeoJSON();
            rectangle.data.geojson.properties = data;
            rectangle.data.geom = JSON.stringify(rectangle.toGeoJSON().geometry);
        }  
        
        // Active la modification via Geoman
        rectangle.pm.enable({
            allowSelfIntersection: false
        });
         
        const padding = this.options.zoomPadding || { paddingTopLeft: [800, 0] };
        this.map.fitBounds(rectangleBounds, padding);
        // this.map.removeLayer(rectangle);
        return rectangle;
    },
    

  };
  
  export default DrawService;
  